import React, { useState, useEffect } from 'react';
import { Container, Grid, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Box, LinearProgress, Card, CardContent, CircularProgress } from '@mui/material';
import { Delete, Restore, Refresh, CheckCircle, Visibility, Search } from '@mui/icons-material';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const Dashboard = () => {
  const [tokens, setTokens] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [memberCount, setMemberCount] = useState('');
  const [priority, setPriority] = useState('activity');
  const [restoreProgress, setRestoreProgress] = useState(null);
  const [alert, setAlert] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [tokenDetails, setTokenDetails] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [showAuditInfo, setShowAuditInfo] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    if (location.state) {
      if (location.state.success) {
        setAlert({ type: 'success', message: location.state.success });
      } else if (location.state.error) {
        setAlert({ type: 'error', message: location.state.error });
      }
      window.history.replaceState({}, document.title);
    }
    loadData();
  }, [location]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [tokensRes, logsRes] = await Promise.all([
        axios.get('http://localhost:5000/api/tokens', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get('http://localhost:5000/api/logs?page=1&limit=100', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setTokens(tokensRes.data);
      setLogs(logsRes.data.logs);
    } catch (error) {
      console.error('Load data error:', error);
      setAlert({ type: 'error', message: 'Failed to load dashboard data. Please try refreshing.' });
    } finally {
      setLoading(false);
    }
  };


  const deleteToken = async (id) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this token? This action cannot be undone.'
    );

    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`http://localhost:5000/api/tokens/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadData();
      setAlert({ type: 'success', message: 'Token deleted successfully' });
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to delete token' });
    }
  };

  const handleRestore = async () => {
    setLoading(true);
    const totalTokens = memberCount ? Math.min(parseInt(memberCount), tokens.length) : tokens.length;
    setRestoreProgress({ current: 0, total: totalTokens, results: [] });

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        'http://localhost:5000/api/restore/guild/placeholder',
        {
          priority,
          inviteLink: inviteLink || undefined,
          memberCount: memberCount ? parseInt(memberCount) : undefined
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setRestoreProgress({
        current: totalTokens,
        total: totalTokens,
        results: response.data.results,
      });

      setAlert({ type: 'success', message: response.data.message });
      loadData();
    } catch (error) {
      if (error.response?.data?.error === 'BOT_NOT_IN_SERVER') {
        setAlert({
          type: 'error',
          message: error.response.data.message,
          inviteUrl: error.response.data.inviteUrl
        });
      } else {
        setAlert({ type: 'error', message: 'Restoration failed' });
      }
    } finally {
      setLoading(false);
      setRestoreDialog(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const validateTokens = async () => {
    setValidating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('http://localhost:5000/api/tokens/validate', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlert({ type: 'success', message: response.data.message });
      await loadData();
    } catch (error) {
      setAlert({ type: 'error', message: 'Token validation failed' });
    } finally {
      setValidating(false);
    }
  };

  const TokenDetails = async (tokenId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`http://localhost:5000/api/tokens/${tokenId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTokenDetails(response.data);
      setDetailsDialog(true);
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to load token details' });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Box textAlign="center">
            <Typography variant="h4" component="h1" gutterBottom>
              Dashboard
            </Typography>
            <Box sx={{ mt: 3, mb: 2 }}>
              <CircularProgress size={60} sx={{ color: '#5865f2' }} />
            </Box>
            <Typography variant="body1" color="text.secondary">
              Loading dashboard data...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refresh}
            disabled={refreshing}
            sx={{ mr: 1 }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CheckCircle />}
            onClick={validateTokens}
            disabled={validating}
            sx={{ mr: 1 }}
          >
            {validating ? 'Validating...' : 'Validate Tokens'}
          </Button>
          <Button variant="outlined" onClick={handleLogout}>
            Logout
          </Button>
        </Box>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
          {alert.inviteUrl && (
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => window.open(alert.inviteUrl, '_blank')}
              >
                Invite Bot to Server
              </Button>
            </Box>
          )}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Analytics Cards */}
        <Grid xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tokens
              </Typography>
              <Typography variant="h4">
                {tokens.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Valid Tokens
              </Typography>
              <Typography variant="h4">
                {tokens.filter(token => token.isValid).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Recent Activity
              </Typography>
              <Typography variant="h4">
                {logs.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Tokens Management */}
        <Grid xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">OAuth2 Tokens</Typography>
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ width: 250 }}
              />
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell>Valid</TableCell>
                    <TableCell>Verified</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokens
                    .filter((token) =>
                      searchQuery === '' ||
                      token.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      token.userId.includes(searchQuery)
                    )
                    .map((token) => (
                      <TableRow key={token._id}>
                        <TableCell>{token.username}#{token.discriminator}</TableCell>
                        <TableCell>{token.userId}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              ...(token.isValid
                                ? {
                                    bgcolor: 'success.main',
                                    color: 'success.contrastText',
                                  }
                                : {
                                    bgcolor: 'error.main',
                                    color: 'error.contrastText',
                                  }
                              ),
                            }}
                          >
                            {token.isValid ? '✓ Valid' : '✗ Invalid'}
                          </Box>
                        </TableCell>
                        <TableCell>{new Date(token.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            onClick={() => TokenDetails(token._id)}
                            sx={{ mr: 1 }}
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => deleteToken(token._id)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Restoration */}
        <Grid xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Member Restoration
            </Typography>
            <Button
              variant="contained"
              startIcon={<Restore />}
              onClick={() => setRestoreDialog(true)}
              disabled={tokens.length === 0}
            >
              Start Restoration
            </Button>
          </Paper>
        </Grid>

        {/* Logs */}
        <Grid xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Recent Activity Logs
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowAllLogs(!showAllLogs)}
              >
                {showAllLogs ? 'Show Less' : 'Show More'}
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Success</TableCell>
                    <TableCell>Timestamp</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.slice(0, showAllLogs ? logs.length : 5).map((log) => (
                    <TableRow key={log._id}>
                      <TableCell>{log.action}</TableCell>
                      <TableCell>{log.tokenId?.username || 'N/A'}</TableCell>
                      <TableCell>{log.success ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Restore Dialog */}
      <Dialog open={restoreDialog} onClose={() => setRestoreDialog(false)}>
        <DialogTitle>Start Member Restoration</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will attempt to restore members to the specified server. Make sure you have the correct permissions and the bot is in the server.
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="Server Invite Link or Guild ID"
            placeholder="https://discord.gg/ABC123 or 123456789012345678"
            fullWidth
            variant="outlined"
            value={inviteLink}
            onChange={(e) => setInviteLink(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Number of Members to Restore (optional)"
            type="number"
            fullWidth
            variant="outlined"
            value={memberCount}
            onChange={(e) => setMemberCount(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          />
          <TextField
            fullWidth
            label="Restoration Notes"
            multiline
            rows={2}
            placeholder="Optional notes for this restoration (e.g., server migration, event restoration, etc.)"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const confirm = window.confirm(
                `Are you sure you want to start restoration? This will attempt to add ${memberCount || 'all'} members to the server.`
              );
              if (confirm) handleRestore();
            }}
            disabled={loading}
            color="primary"
            variant="contained"
          >
            {loading ? 'Starting...' : 'Start Restoration'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Token Details Dialog */}
      <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" component="div">
              Token Details
            </Typography>
            {tokenDetails && tokenDetails.avatar && (
              <Box
                component="img"
                src={`https://cdn.discordapp.com/avatars/${tokenDetails.userId}/${tokenDetails.avatar}.png?size=256`}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  border: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {tokenDetails && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Basic Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Username</Typography>
                        <Typography variant="body1">{tokenDetails.username}#{tokenDetails.discriminator}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">User ID</Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{tokenDetails.userId}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Typography variant="body1" color={tokenDetails.isValid ? 'success.main' : 'error.main'}>
                          {tokenDetails.isValid ? 'Valid' : 'Invalid'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Token Statistics
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Restorations Used</Typography>
                        <Typography variant="body2">{tokenDetails.activityLevel || 0}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Success Rate</Typography>
                        <Typography variant="body2" color="success.main">
                          {tokenDetails.activityLevel > 0 ? '100%' : 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Token Age</Typography>
                        <Typography variant="body2">
                          {Math.floor((new Date() - new Date(tokenDetails.createdAt)) / (1000 * 60 * 60 * 24))} days
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Last Activity</Typography>
                        <Typography variant="body2">
                          {tokenDetails.lastUsed !== tokenDetails.createdAt ?
                            `${Math.floor((new Date() - new Date(tokenDetails.lastUsed)) / (1000 * 60 * 60 * 24))} days ago` :
                            'Never used'
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Security & Audit Information
                    </Typography>
                    <Box sx={{
                      p: 3,
                      textAlign: 'center',
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: 1,
                      filter: showAuditInfo ? 'none' : 'blur(4px)',
                      userSelect: showAuditInfo ? 'text' : 'none',
                      transition: 'filter 0.3s ease'
                    }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">IP Address</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{tokenDetails.ipAddress || 'Not recorded'}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Token ID</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{tokenDetails._id}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">User Agent</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-word' }}>
                              {tokenDetails.userAgent || 'Not recorded'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowAuditInfo(!showAuditInfo)}
                      >
                        {showAuditInfo ? 'Hide sensitive data' : 'Click to reveal'}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Progress Dialog */}
      {restoreProgress && (
        <Dialog open={true} maxWidth="md" fullWidth>
          <DialogTitle>Restoration Progress</DialogTitle>
          <DialogContent>
            <LinearProgress
              variant="determinate"
              value={(restoreProgress.current / restoreProgress.total) * 100}
              sx={{ mb: 2 }}
            />
            <Typography>
              {restoreProgress.current} / {restoreProgress.total} completed
            </Typography>
            {restoreProgress.results.length > 0 && (
              <TableContainer sx={{ mt: 2, maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {restoreProgress.results.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell>{result.username}</TableCell>
                        <TableCell>{result.status}</TableCell>
                        <TableCell>{result.error || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRestoreProgress(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default Dashboard;